import type { GraphDocument, NodeRecord, EdgeRecord } from '@/domain/funnel/graph-document';
import { safeDataValue } from './safe-value';

const PRIVATE_KEYS = new Set([
  'userId',
  'ownerId',
  'auth',
  'session',
  'share',
  'shareToken',
  'tokenHash',
  'passwordHash',
  'tracking',
  'analytics',
  'recovery',
  'history',
  'snapshots',
  'audit',
  'storageKey',
  'r2Key',
  'assetKey',
  'attachments',
  'companyLogoAssetId',
  '__editorOriginalType',
  '__editorLocked',
]);

function sanitizeRecord(value: Record<string, unknown>): Record<string, unknown> {
  const clean: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value)) {
    if (!PRIVATE_KEYS.has(key)) clean[key] = safeDataValue(nested);
  }
  return clean;
}

function sanitizeNode(node: NodeRecord): NodeRecord {
  const clean = sanitizeRecord(node);
  return {
    ...clean,
    id: node.id,
    type: node.type,
    position: { x: node.position.x, y: node.position.y },
    data: sanitizeRecord(node.data),
  } as NodeRecord;
}

function sanitizeEdge(edge: EdgeRecord): EdgeRecord {
  const clean = sanitizeRecord(edge);
  return {
    ...clean,
    id: edge.id,
    type: edge.type,
    source: edge.source,
    target: edge.target,
    data: sanitizeRecord(edge.data),
  } as EdgeRecord;
}

export function sanitizeGraphForExport(graph: GraphDocument): GraphDocument {
  const clean = sanitizeRecord(graph);
  const presentation =
    graph.presentation && typeof graph.presentation === 'object'
      ? sanitizeRecord(graph.presentation as Record<string, unknown>)
      : undefined;
  return {
    ...clean,
    schemaVersion: graph.schemaVersion,
    nodes: graph.nodes.map(sanitizeNode),
    edges: graph.edges.map(sanitizeEdge),
    viewport: { ...graph.viewport },
    ...(presentation ? { presentation } : {}),
  } as GraphDocument;
}
